import React, { useCallback, useEffect, useState } from "react";
import {
  Plus,
  Loader2,
  HelpCircle,
  Trash2,
  Edit2,
  ArrowUp,
  ArrowDown,
  Check,
  X,
  Download,
} from "lucide-react";
import Notiflix from "notiflix";
import {
  getFaqsAPI,
  createFaqAPI,
  bulkCreateFaqsAPI,
  updateFaqAPI,
  deleteFaqAPI,
  reorderFaqsAPI,
} from "../services/apicollection";

/**
 * FAQ CRUD — ek hi component 2 jagah use hota hai:
 *   <FaqManager course="general" />        -> Home page FAQ
 *   <FaqManager course={courseId} />       -> Course Content form ke andar, us course ke FAQ
 *
 * IMPORTANT: ye component CourseContentForm ke <form> ke ANDAR render hota hai,
 * isliye yahan koi <form> nahi hai, saare buttons type="button" hain aur input
 * me Enter dabane par parent form submit nahi hota.
 *
 * legacyFaq (optional): purana course-content wala FAQ [{category, questions:[{que, ans}]}]
 * — agar naye FAQ khali hain to ek click me import karne ka option dikhta hai.
 */

const inputCls =
  "w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm focus:outline-none focus:border-[#1ec1d1]";

const errMsg = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

// input me Enter -> parent form submit mat karo
const blockEnter = (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    e.stopPropagation();
  }
};

const FaqManager = ({ course, legacyFaq = [], emptyHint }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [importing, setImporting] = useState(false);

  const fetchFaqs = useCallback(
    async (signal) => {
      if (!course) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getFaqsAPI({ course, signal });
        if (res?.status) {
          setFaqs(Array.isArray(res.data) ? res.data : []);
        } else {
          setFaqs([]);
          setError(res?.message || "Failed to load FAQs");
        }
      } catch (err) {
        if (err?.name === "CanceledError") return;
        setFaqs([]);
        setError(errMsg(err, "Failed to load FAQs"));
      } finally {
        setLoading(false);
      }
    },
    [course],
  );

  useEffect(() => {
    setAdding(false);
    setEditingId(null);
    setFaqs([]);
    if (!course) return;
    const controller = new AbortController();
    fetchFaqs(controller.signal);
    return () => controller.abort();
  }, [course, fetchFaqs]);

  // ---------- course select nahi hua ----------
  if (!course) {
    return (
      <div className="rounded-lg border border-dashed border-[#1d3350] px-4 py-6 text-center text-sm text-slate-400">
        {emptyHint || "FAQ add karne ke liye pehle upar se course select karo."}
      </div>
    );
  }

  // ---------- actions ----------
  const handleCreate = async ({ question, answer, isActive }) => {
    const res = await createFaqAPI({ question, answer, isActive, course });
    if (!res?.status) throw new Error(res?.message || "Failed to add FAQ");
    setFaqs((prev) => [...prev, res.data]);
    Notiflix.Notify.success("FAQ added");
  };

  const handleUpdate = async (id, payload) => {
    const res = await updateFaqAPI(id, payload);
    if (!res?.status) throw new Error(res?.message || "Failed to update FAQ");
    setFaqs((prev) => prev.map((f) => (f._id === id ? { ...f, ...res.data } : f)));
    setEditingId(null);
    Notiflix.Notify.success("FAQ updated");
  };

  const handleToggle = async (faq) => {
    setBusyId(faq._id);
    try {
      const res = await updateFaqAPI(faq._id, { isActive: !faq.isActive });
      if (res?.status) {
        setFaqs((prev) =>
          prev.map((f) =>
            f._id === faq._id ? { ...f, isActive: !faq.isActive } : f,
          ),
        );
      } else {
        Notiflix.Notify.failure(res?.message || "Failed to update");
      }
    } catch (err) {
      Notiflix.Notify.failure(errMsg(err, "Failed to update"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = (faq) => {
    Notiflix.Confirm.show(
      "Delete FAQ",
      "Ye FAQ permanently delete ho jayega. Continue?",
      "Delete",
      "Cancel",
      async () => {
        setBusyId(faq._id);
        try {
          const res = await deleteFaqAPI(faq._id);
          if (res?.status) {
            setFaqs((prev) => prev.filter((f) => f._id !== faq._id));
            Notiflix.Notify.success("FAQ deleted");
          } else {
            Notiflix.Notify.failure(res?.message || "Failed to delete");
          }
        } catch (err) {
          Notiflix.Notify.failure(errMsg(err, "Failed to delete"));
        } finally {
          setBusyId(null);
        }
      },
    );
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= faqs.length || reordering) return;
    const prev = faqs;
    const next = [...faqs];
    [next[index], next[target]] = [next[target], next[index]];
    setFaqs(next); // optimistic
    setReordering(true);
    try {
      const res = await reorderFaqsAPI(next.map((f) => f._id));
      if (!res?.status) {
        setFaqs(prev);
        Notiflix.Notify.failure(res?.message || "Failed to reorder");
      }
    } catch (err) {
      setFaqs(prev);
      Notiflix.Notify.failure(errMsg(err, "Failed to reorder"));
    } finally {
      setReordering(false);
    }
  };

  // purane course-content FAQ -> naya system
  const legacyItems = (Array.isArray(legacyFaq) ? legacyFaq : [])
    .flatMap((c) => c?.questions || [])
    .map((q) => ({ question: (q?.que || "").trim(), answer: (q?.ans || "").trim() }))
    .filter((q) => q.question && q.answer);

  const handleImportLegacy = async () => {
    setImporting(true);
    try {
      const res = await bulkCreateFaqsAPI(course, legacyItems);
      if (res?.status) {
        Notiflix.Notify.success(res.message || "FAQs imported");
        fetchFaqs();
      } else {
        Notiflix.Notify.failure(res?.message || "Import failed");
      }
    } catch (err) {
      Notiflix.Notify.failure(errMsg(err, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const activeCount = faqs.filter((f) => f.isActive).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-400">
          {faqs.length} FAQ · {activeCount} website par visible
          <span className="ml-1 text-slate-500">(changes turant save hote hain)</span>
        </p>
        {!adding && (
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setAdding(true);
            }}
            className="admin-btn-primary inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm"
          >
            <Plus size={15} />
            Add FAQ
          </button>
        )}
      </div>

      {adding && (
        <FaqEditor
          submitLabel="Save FAQ"
          allowAddAnother
          onCancel={() => setAdding(false)}
          onSubmit={handleCreate}
        />
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-lg border border-[#1d3350] py-10">
          <Loader2 size={24} className="animate-spin text-slate-400" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-center text-sm text-red-300">
          {error}
          <button
            type="button"
            onClick={() => fetchFaqs()}
            className="ml-2 underline"
          >
            Retry
          </button>
        </div>
      ) : faqs.length === 0 ? (
        !adding && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-[#1d3350] py-8 text-center">
            <HelpCircle size={30} className="text-slate-500" />
            <p className="mt-2 text-sm text-slate-400">Abhi koi FAQ nahi hai</p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="text-sm font-medium text-[#1ec1d1] underline hover:no-underline"
              >
                Pehla FAQ add karo
              </button>
              {legacyItems.length > 0 && (
                <button
                  type="button"
                  onClick={handleImportLegacy}
                  disabled={importing}
                  className="inline-flex items-center gap-1 rounded border border-[#1d3350] px-2.5 py-1 text-xs text-slate-300 hover:bg-[#13233a] disabled:opacity-60"
                >
                  {importing ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Download size={12} />
                  )}
                  Purane {legacyItems.length} FAQ import karo
                </button>
              )}
            </div>
          </div>
        )
      ) : (
        <ul className="space-y-2">
          {faqs.map((f, i) =>
            editingId === f._id ? (
              <li key={f._id}>
                <FaqEditor
                  initial={f}
                  submitLabel="Update"
                  onCancel={() => setEditingId(null)}
                  onSubmit={(payload) => handleUpdate(f._id, payload)}
                />
              </li>
            ) : (
              <li
                key={f._id}
                className={`rounded-lg border border-[#1d3350] bg-[#0d1220] p-3 transition-opacity ${
                  f.isActive ? "" : "opacity-60"
                }`}
              >
                <div className="flex gap-3">
                  <div className="flex shrink-0 flex-col items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => move(i, -1)}
                      disabled={i === 0 || reordering}
                      title="Upar"
                      className="rounded p-1 text-slate-400 hover:bg-[#13233a] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <span className="text-[11px] font-semibold text-[#1ec1d1]">
                      {i + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => move(i, 1)}
                      disabled={i === faqs.length - 1 || reordering}
                      title="Neeche"
                      className="rounded p-1 text-slate-400 hover:bg-[#13233a] hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      <ArrowDown size={14} />
                    </button>
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-100">{f.question}</p>
                    <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-slate-400">
                      {f.answer}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#17283f] pt-2">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-slate-400">Show on site:</span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={f.isActive}
                          onClick={() => handleToggle(f)}
                          disabled={busyId === f._id}
                          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none disabled:opacity-60 ${
                            f.isActive ? "bg-cyan-600" : "bg-slate-600"
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-[#0f1c2e] transition-transform ${
                              f.isActive ? "translate-x-4" : "translate-x-0.5"
                            }`}
                          />
                        </button>
                        <span className="font-medium text-slate-300">
                          {f.isActive ? "Yes" : "No"}
                        </span>
                      </div>

                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setAdding(false);
                            setEditingId(f._id);
                          }}
                          className="rounded border border-[#1d3350] px-2 py-1 text-xs text-slate-300 hover:bg-[#13233a]"
                        >
                          <Edit2 size={12} className="inline" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(f)}
                          disabled={busyId === f._id}
                          className="rounded border border-red-500/40 px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 disabled:opacity-60"
                        >
                          <Trash2 size={12} className="inline" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ),
          )}
        </ul>
      )}
    </div>
  );
};

// =====================================================================
// Inline add / edit box  (no <form> — parent form ke andar safe)
// =====================================================================
const FaqEditor = ({ initial, submitLabel, allowAddAnother, onCancel, onSubmit }) => {
  const [question, setQuestion] = useState(initial?.question || "");
  const [answer, setAnswer] = useState(initial?.answer || "");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [addAnother, setAddAnother] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState("");

  const submit = async () => {
    if (!question.trim() || !answer.trim()) {
      setFieldError("Question aur answer dono required hain");
      return;
    }
    setFieldError("");
    setSaving(true);
    try {
      await onSubmit({
        question: question.trim(),
        answer: answer.trim(),
        isActive,
      });
      if (allowAddAnother && addAnother) {
        setQuestion("");
        setAnswer("");
      } else if (allowAddAnother) {
        onCancel();
      }
    } catch (err) {
      Notiflix.Notify.failure(errMsg(err, "Failed to save FAQ"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-[#1ec1d1]/50 bg-[#0d1b2e] p-3">
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-400">
          Question *
        </label>
        <input
          type="text"
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
            setFieldError("");
          }}
          onKeyDown={blockEnter}
          placeholder="e.g. What's the duration and fee?"
          className={inputCls}
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-slate-400">
          Answer *
        </label>
        <textarea
          value={answer}
          onChange={(e) => {
            setAnswer(e.target.value);
            setFieldError("");
          }}
          rows={4}
          placeholder="Answer likho… (new line ke liye Enter)"
          className={`${inputCls} resize-y`}
        />
      </div>

      {fieldError && <p className="text-xs text-red-400">{fieldError}</p>}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 accent-[#1ec1d1]"
            />
            Show on site
          </label>
          {allowAddAnother && (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={addAnother}
                onChange={(e) => setAddAnother(e.target.checked)}
                className="h-4 w-4 accent-[#1ec1d1]"
              />
              Save karke next add karo
            </label>
          )}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-lg border border-[#1d3350] px-3 py-1.5 text-sm text-slate-300 hover:bg-[#13233a]"
          >
            <X size={14} />
            {allowAddAnother && addAnother ? "Done" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="admin-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm disabled:opacity-60"
          >
            {saving ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Check size={14} />
            )}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaqManager;